/**
 * Turning what a founder types into the integer cents core stores.
 *
 * Money is integer cents at every boundary (canon `docs/ARCHITECTURE.md` § API style), and
 * `POST /v1/seller/bids` refuses a float, a numeric string or a non-integer rather than
 * rounding it. A bid box is free text, so this is where `$1.50` becomes `150` and where
 * anything that is not an amount is refused before a request is sent.
 *
 * The conversion never multiplies a parsed float by 100: `parseFloat("19.99") * 100` is
 * `1998.9999999999998`, and money is not a value to be approximately right about. The whole
 * and fractional parts are read as separate integers instead.
 *
 * This is a check, not the authority. Core validates the amount again, applies the auction's
 * own minimum and decides whether the credit is there (`docs/SECURITY.md` § Credits/bids).
 * Answering here only saves a round trip to say what the browser already knew.
 */

/** Core's upper bound on any monetary field ($1,000,000) — `MAX_BID_CENTS` in dealbid-core. */
export const MAX_BID_CENTS = 100_000_000;

export type BidAmountParse =
  | { ok: true; cents: number }
  | { ok: false; message: string };

const AMOUNT_PATTERN = /^\d{1,12}(?:\.\d{1,2})?$/;

/**
 * Parses a bid amount in dollars into cents.
 *
 * Unlike a submitted price, an empty bid box is not a valid absence: there is exactly one
 * field here and it is the whole request.
 */
export function parseBidAmount(raw: string): BidAmountParse {
  const trimmed = raw.trim();
  if (trimmed === "") return { ok: false, message: "Enter the amount you want to bid." };

  // A currency symbol and thousands separators are what people actually type; they carry no
  // information, so they are removed rather than rejected.
  const cleaned = trimmed.replace(/^\$/, "").replace(/,/g, "").trim();
  if (!AMOUNT_PATTERN.test(cleaned)) {
    return { ok: false, message: "Enter an amount in dollars, like 5 or 5.50." };
  }

  const [whole = "0", fraction = ""] = cleaned.split(".");
  const cents = Number(whole) * 100 + Number(fraction.padEnd(2, "0"));

  if (!Number.isSafeInteger(cents) || cents > MAX_BID_CENTS) {
    return { ok: false, message: "Enter an amount of $1,000,000 or less." };
  }
  return { ok: true, cents };
}

export type PriceAmountParse =
  | { ok: true; cents: number | null }
  | { ok: false; message: string };

/**
 * Parses a deal price in dollars into cents, where **empty means cleared**.
 *
 * This is the difference from {@link parseBidAmount}, and it is a contract difference
 * rather than a leniency: a bid box is the whole request and an empty one is nothing to
 * send, while a price is nullable in the deal itself. `contracts/API.md` § Seller deal edit
 * shape makes an explicit `null` the way a founder clears a nullable field, so an emptied
 * price box has to mean something — and the only honest meaning is "this deal no longer
 * states one".
 *
 * Canon § 2 still requires the merged deal to carry either both prices or a written value
 * claim. That check spans fields and belongs to core, which evaluates it against the merged
 * deal; a box emptied here is refused there with the field named, not silently accepted.
 */
export function parsePriceAmount(raw: string): PriceAmountParse {
  if (raw.trim() === "") return { ok: true, cents: null };

  const parsed = parseBidAmount(raw);
  if (parsed.ok) return { ok: true, cents: parsed.cents };
  // The amount parser's "enter an amount" sentence asks for a bid and does not mention that
  // a price may be left out, so only its verdict is reused. Its upper-bound sentence says
  // the same thing to a price as it does to a bid, and is passed through unchanged.
  return /1,000,000/.test(parsed.message)
    ? { ok: false, message: parsed.message }
    : {
        ok: false,
        message: "Enter a price in dollars, like 49 or 49.99, or leave it empty.",
      };
}
